/** Polls keyboard state each frame and exposes a structured InputState reflecting all game actions. */
import Phaser from 'phaser';
import { Component } from './Component';
import { KeyBindings, GameAction } from '../systems/KeyBindings';

export interface InputState {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  run: boolean;
  dash: boolean;
  skill1: boolean;
  skill2: boolean;
  skill3: boolean;
  skill4: boolean;
  skill5: boolean;
}

/** Register primary and alternate keys for a GameAction, skipping UNBOUND (-1) key codes. */
function registerAction(
  kb: Phaser.Input.Keyboard.KeyboardPlugin,
  action: GameAction,
): Phaser.Input.Keyboard.Key[] {
  const binding = KeyBindings.getBinding(action);
  const keys: Phaser.Input.Keyboard.Key[] = [];
  if (binding.primary >= 0) {
    keys.push(kb.addKey(binding.primary));
  }
  if (binding.alternate !== undefined && binding.alternate >= 0) {
    keys.push(kb.addKey(binding.alternate));
  }
  return keys;
}

function anyDown(keys: Phaser.Input.Keyboard.Key[]): boolean {
  return keys.some(k => k.isDown);
}

function anyJustDown(keys: Phaser.Input.Keyboard.Key[]): boolean {
  return keys.some(k => Phaser.Input.Keyboard.JustDown(k));
}

/** Reads KeyBindings-driven key assignments each frame and writes the results into a flat InputState object. */
export class InputComponent implements Component {
  private moveUpKeys: Phaser.Input.Keyboard.Key[];
  private moveDownKeys: Phaser.Input.Keyboard.Key[];
  private moveLeftKeys: Phaser.Input.Keyboard.Key[];
  private moveRightKeys: Phaser.Input.Keyboard.Key[];
  private runKeys: Phaser.Input.Keyboard.Key[];
  private dashKeys: Phaser.Input.Keyboard.Key[];
  private skill1Keys: Phaser.Input.Keyboard.Key[];
  private skill2Keys: Phaser.Input.Keyboard.Key[];
  private skill3Keys: Phaser.Input.Keyboard.Key[];
  private skill4Keys: Phaser.Input.Keyboard.Key[];
  private skill5Keys: Phaser.Input.Keyboard.Key[];

  readonly state: InputState = {
    up: false,
    down: false,
    left: false,
    right: false,
    run: false,
    dash: false,
    skill1: false,
    skill2: false,
    skill3: false,
    skill4: false,
    skill5: false,
  };

  /**
   * When non-null, the sim override supplies directional input instead of keyboard polling.
   * Set by SimulationBridge.setPlayerMoving(). Keyboard keys are still read for non-movement
   * actions (run, dash, skills) to allow mixed sim+keyboard testing.
   */
  simMovementOverride: { up: boolean; down: boolean; left: boolean; right: boolean } | null = null;

  constructor(scene: Phaser.Scene) {
    if (!scene.input.keyboard) {
      throw new Error('Keyboard input not available');
    }

    const kb = scene.input.keyboard;
    this.moveUpKeys = registerAction(kb, GameAction.MoveUp);
    this.moveDownKeys = registerAction(kb, GameAction.MoveDown);
    this.moveLeftKeys = registerAction(kb, GameAction.MoveLeft);
    this.moveRightKeys = registerAction(kb, GameAction.MoveRight);
    this.runKeys = registerAction(kb, GameAction.Run);
    this.dashKeys = registerAction(kb, GameAction.Dash);
    this.skill1Keys = registerAction(kb, GameAction.Skill1);
    this.skill2Keys = registerAction(kb, GameAction.Skill2);
    this.skill3Keys = registerAction(kb, GameAction.Skill3);
    this.skill4Keys = registerAction(kb, GameAction.Skill4);
    this.skill5Keys = registerAction(kb, GameAction.Skill5);
  }

  update(_delta: number): void {
    if (this.simMovementOverride) {
      this.state.up = this.simMovementOverride.up;
      this.state.down = this.simMovementOverride.down;
      this.state.left = this.simMovementOverride.left;
      this.state.right = this.simMovementOverride.right;
    } else {
      this.state.up = anyDown(this.moveUpKeys);
      this.state.down = anyDown(this.moveDownKeys);
      this.state.left = anyDown(this.moveLeftKeys);
      this.state.right = anyDown(this.moveRightKeys);
    }
    this.state.run = anyDown(this.runKeys);
    this.state.dash = anyJustDown(this.dashKeys);
    this.state.skill1 = anyJustDown(this.skill1Keys);
    this.state.skill2 = anyJustDown(this.skill2Keys);
    this.state.skill3 = anyJustDown(this.skill3Keys);
    this.state.skill4 = anyJustDown(this.skill4Keys);
    this.state.skill5 = anyJustDown(this.skill5Keys);
  }

  destroy(): void {
    // Keys are managed by Phaser's input system
  }
}
